import type { Person, Relationship } from './types'

/**
 * Graph layout engine for family constellations.
 *
 * Layered (generation-ranked) layout in the spirit of graph-based UI engines:
 * 1. rank: longest-path generation assignment with spouse alignment
 * 2. order: children gravitate under parents, spouses pulled adjacent
 * 3. position: barycenter sweep + collision resolution, rows centered
 * 4. edges: spouse connectors + orthogonal parent->child drops from union midpoints
 */

export const NODE_W = 170
export const NODE_H = 64
const H_GAP = 44
const V_GAP = 120

export interface LayoutNode {
  person: Person
  x: number // left
  y: number // top
  gen: number
}

export interface SpouseEdge {
  id: string
  x1: number
  x2: number
  y: number
  midX: number
}

export interface ChildEdge {
  id: string
  fromX: number
  fromY: number
  toX: number
  toY: number
}

export interface TreeLayout {
  nodes: LayoutNode[]
  spouseEdges: SpouseEdge[]
  childEdges: ChildEdge[]
  width: number
  height: number
}

export function layoutTree(persons: Person[], relationships: Relationship[]): TreeLayout {
  if (persons.length === 0) return { nodes: [], spouseEdges: [], childEdges: [], width: 800, height: 400 }

  const byId = new Map(persons.map(p => [p.id, p]))
  const parentsOf = new Map<string, string[]>()
  const childrenOf = new Map<string, string[]>()
  const spousesOf = new Map<string, string[]>()

  for (const r of relationships) {
    if (!byId.has(r.person1_id) || !byId.has(r.person2_id)) continue
    if (r.type === 'parent') {
      parentsOf.set(r.person2_id, [...(parentsOf.get(r.person2_id) || []), r.person1_id])
      childrenOf.set(r.person1_id, [...(childrenOf.get(r.person1_id) || []), r.person2_id])
    } else {
      spousesOf.set(r.person1_id, [...(spousesOf.get(r.person1_id) || []), r.person2_id])
      spousesOf.set(r.person2_id, [...(spousesOf.get(r.person2_id) || []), r.person1_id])
    }
  }

  // ── 1. Generation ranking ──
  const gen = new Map<string, number>(persons.map(p => [p.id, 0]))
  for (let pass = 0; pass < 60; pass++) {
    let changed = false
    for (const p of persons) {
      for (const par of parentsOf.get(p.id) || []) {
        const want = (gen.get(par) || 0) + 1
        if ((gen.get(p.id) || 0) < want) { gen.set(p.id, want); changed = true }
      }
      for (const sp of spousesOf.get(p.id) || []) {
        const g = Math.max(gen.get(p.id) || 0, gen.get(sp) || 0)
        if ((gen.get(p.id) || 0) !== g) { gen.set(p.id, g); changed = true }
        if ((gen.get(sp) || 0) !== g) { gen.set(sp, g); changed = true }
      }
    }
    if (!changed) break
  }

  const minGen = Math.min(...gen.values())
  for (const [id, g] of gen) gen.set(id, g - minGen)
  const maxGen = Math.max(...gen.values())

  // ── 2. Ordering within generations ──
  const rows: string[][] = Array.from({ length: maxGen + 1 }, () => [])
  for (const p of persons) rows[gen.get(p.id)!].push(p.id)

  const orderIndex = new Map<string, number>()
  rows.forEach(row => row.forEach((id, i) => orderIndex.set(id, i)))

  for (let sweep = 0; sweep < 4; sweep++) {
    for (let g = 1; g <= maxGen; g++) {
      // barycenter of parents
      rows[g].sort((a, b) => {
        const av = avgOrder(parentsOf.get(a) || [], orderIndex, orderIndex.get(a)!)
        const bv = avgOrder(parentsOf.get(b) || [], orderIndex, orderIndex.get(b)!)
        return av - bv
      })
      pullSpousesAdjacent(rows[g], spousesOf)
      rows[g].forEach((id, i) => orderIndex.set(id, i))
    }
    for (let g = maxGen - 1; g >= 0; g--) {
      rows[g].sort((a, b) => {
        const av = avgOrder(childrenOf.get(a) || [], orderIndex, orderIndex.get(a)!)
        const bv = avgOrder(childrenOf.get(b) || [], orderIndex, orderIndex.get(b)!)
        return av - bv
      })
      pullSpousesAdjacent(rows[g], spousesOf)
      rows[g].forEach((id, i) => orderIndex.set(id, i))
    }
  }

  // ── 3. X positioning: barycenter targets + tight min-gap packing ──
  // Each node is pulled toward its parents (down sweep) / children (up sweep) plus its
  // spouse; then every row is packed to the minimum gap and recentered on that pull.
  // Packing (not just push-right collision) is what stops the tree from sprawling —
  // empty gaps between loosely-connected branches are closed each pass.
  const STEP = NODE_W + H_GAP
  const x = new Map<string, number>()
  rows.forEach(row => row.forEach((id, i) => x.set(id, i * STEP)))

  const targetFrom = (id: string, rel: Map<string, string[]>): number => {
    const kin = [...(rel.get(id) || []), ...(spousesOf.get(id) || [])]
      .map(r => x.get(r)).filter((v): v is number => v !== undefined)
    return kin.length ? kin.reduce((s, v) => s + v, 0) / kin.length : x.get(id)!
  }

  for (let sweep = 0; sweep < 8; sweep++) {
    const down = sweep % 2 === 0
    const gens = down ? Array.from({ length: maxGen + 1 }, (_, g) => g)
                      : Array.from({ length: maxGen + 1 }, (_, g) => maxGen - g)
    for (const g of gens) {
      const targets = new Map(rows[g].map(id => [id, targetFrom(id, down ? parentsOf : childrenOf)]))
      packRow(rows[g], x, targets, STEP)
    }
  }

  // normalize to start at 40
  const minX = Math.min(...x.values())
  for (const [id, v] of x) x.set(id, v - minX + 40)

  const nodes: LayoutNode[] = persons.map(p => ({
    person: p,
    x: x.get(p.id) || 40,
    y: (gen.get(p.id) || 0) * (NODE_H + V_GAP) + 40,
    gen: gen.get(p.id) || 0,
  }))
  const nodeById = new Map(nodes.map(n => [n.person.id, n]))

  // ── 4. Edges ──
  const spouseEdges: SpouseEdge[] = []
  const seenSpouse = new Set<string>()
  for (const r of relationships) {
    if (r.type !== 'spouse') continue
    const key = [r.person1_id, r.person2_id].sort().join('|')
    if (seenSpouse.has(key)) continue
    seenSpouse.add(key)
    const a = nodeById.get(r.person1_id), b = nodeById.get(r.person2_id)
    if (!a || !b) continue
    const [left, right] = a.x <= b.x ? [a, b] : [b, a]
    spouseEdges.push({
      id: r.id,
      x1: left.x + NODE_W,
      x2: right.x,
      y: left.y + NODE_H / 2,
      midX: (left.x + NODE_W + right.x) / 2,
    })
  }

  const childEdges: ChildEdge[] = []
  for (const p of persons) {
    const pars = (parentsOf.get(p.id) || []).map(id => nodeById.get(id)).filter(Boolean) as LayoutNode[]
    if (!pars.length) continue
    const child = nodeById.get(p.id)!
    let fromX: number, fromY: number
    // If two of the child's parents are a couple on the same row, descend the child
    // from the MIDPOINT of their marriage bar (so a couple's children — and siblings —
    // hang centered under the marriage, not off one parent).
    let couple: [LayoutNode, LayoutNode] | null = null
    for (let i = 0; i < pars.length && !couple; i++) {
      for (let j = i + 1; j < pars.length; j++) {
        const spouses = (spousesOf.get(pars[i].person.id) || []).includes(pars[j].person.id)
        if (spouses && Math.abs(pars[i].y - pars[j].y) < 1) { couple = [pars[i], pars[j]]; break }
      }
    }
    if (couple) {
      const [l, r] = couple[0].x <= couple[1].x ? couple : [couple[1], couple[0]]
      fromX = (l.x + NODE_W + r.x) / 2
      fromY = l.y + NODE_H / 2
    } else if (pars.length >= 2) {
      fromX = pars.reduce((s, n) => s + n.x + NODE_W / 2, 0) / pars.length
      fromY = Math.max(...pars.map(n => n.y)) + NODE_H
    } else {
      fromX = pars[0].x + NODE_W / 2
      fromY = pars[0].y + NODE_H
    }
    childEdges.push({
      id: `ce-${p.id}`,
      fromX, fromY,
      toX: child.x + NODE_W / 2,
      toY: child.y,
    })
  }

  const width = Math.max(...nodes.map(n => n.x + NODE_W)) + 40
  const height = Math.max(...nodes.map(n => n.y + NODE_H)) + 40

  return { nodes, spouseEdges, childEdges, width, height }
}

function avgOrder(ids: string[], orderIndex: Map<string, number>, fallback: number): number {
  const vals = ids.map(id => orderIndex.get(id)).filter(v => v !== undefined) as number[]
  if (!vals.length) return fallback
  return vals.reduce((s, v) => s + v, 0) / vals.length
}

function pullSpousesAdjacent(row: string[], spousesOf: Map<string, string[]>) {
  for (let i = 0; i < row.length; i++) {
    const spouses = spousesOf.get(row[i]) || []
    for (const sp of spouses) {
      const j = row.indexOf(sp)
      if (j > i + 1) {
        row.splice(j, 1)
        row.splice(i + 1, 0, sp)
      }
    }
  }
}

/**
 * Place a generation's nodes at their pull targets, enforce the minimum gap in
 * row order (so they never overlap), then shift the whole row so its mean matches
 * the target mean. The shift preserves the min-gap (everyone moves equally) while
 * pulling the tightly-packed row back to where its branches want it — compact, no
 * sprawl, still roughly under its parents/children.
 */
function packRow(order: string[], x: Map<string, number>, targets: Map<string, number>, step: number) {
  for (const id of order) x.set(id, targets.get(id)!)
  for (let i = 1; i < order.length; i++) {
    const prev = x.get(order[i - 1])!
    if (x.get(order[i])! < prev + step) x.set(order[i], prev + step)
  }
  let drift = 0
  for (const id of order) drift += targets.get(id)! - x.get(id)!
  drift /= order.length || 1
  for (const id of order) x.set(id, x.get(id)! + drift)
}
